import { createRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { createAuthToken } from '@/api/internal-token-client'
import { fetchCardholderName } from '@/api/transactions-client'
import { AdyenDropin, ApplePayButton } from '@/components/ui/adyen'
import { Button } from '@/components/ui/button'
import { useHppTheme } from '@/hooks/use-hpp-theme'
import { checkoutFieldModeToCardConfig } from '@/lib/checkout-field-mode'
import { SessionType } from '@/lib/enums'
import { env } from '@/lib/env'
import { expectedHppOrigin } from '@/lib/hpp-origin'
import { postToHost } from '@/lib/post-message'

import { Route as rootRoute } from './__root'

import type { AdyenDropinHandle } from '@/components/ui/adyen'
import type { CheckoutFieldMode } from '@/lib/checkout-field-mode'
import type { ErrorWithState, HppSessionConfig, SessionResult } from '@/lib/types'

// TanStack Router's default `parseSearch` runs `JSON.parse` per value, so
// `?billingInfo=0` reaches `validateSearch` as the number `0` — not the
// string `'0'`. Pre-stringify before the literal check so both shapes pass
// and the downstream comparisons can keep talking in strings.
const stringifiedLiteral = <T extends string>(literal: T) =>
  z.preprocess((v) => (v == null ? v : String(v)), z.literal(literal)).optional()

const searchSchema = z.object({
  sessionData: z.string().optional(),
  transactionId: z.string().optional(),
  theme: z.string().optional(),
  showButton: stringifiedLiteral('1'),
  billingInfo: stringifiedLiteral('0'),
})

const hppPayloadSchema = z.object({
  theme: z.object({
    primaryColor: z.string(),
    secondaryColor: z.string(),
  }),
  paymentButtonLabel: z.string(),
  collectPayerInfo: z.boolean(),
  lang: z.string().optional(),
  checkoutFieldMode: z.enum(['default', 'zip', 'full']).optional(),
  parentHost: z.string().optional(),
})

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hpp-session',
  validateSearch: (search) => searchSchema.parse(search),
  component: HppSessionPage,
})

function HppSessionPage() {
  const { sessionData, transactionId, theme, showButton, billingInfo } = Route.useSearch()

  const config = useMemo<HppSessionConfig | null>(() => {
    if (!theme) return null
    try {
      const bytes = Uint8Array.from(atob(theme), (c) => c.charCodeAt(0))
      const json = new TextDecoder().decode(bytes)
      return hppPayloadSchema.parse(JSON.parse(json))
    } catch {
      return null
    }
  }, [theme])

  if (!sessionData || !config) return <IdleState />
  return (
    <HppSessionForm
      sessionData={sessionData}
      transactionId={transactionId}
      config={config}
      showButton={showButton === '1'}
      collectBillingInfo={billingInfo !== '0'}
    />
  )
}

function IdleState() {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <p className="text-foreground text-sm">Invalid URL parameters.</p>
    </div>
  )
}

interface FormProps {
  sessionData: string
  transactionId?: string
  config: HppSessionConfig
  showButton: boolean
  collectBillingInfo: boolean
}

function HppSessionForm({ sessionData, transactionId, config, showButton, collectBillingInfo }: FormProps) {
  const [token, setToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<Error | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [applePayAvailable, setApplePayAvailable] = useState(false)
  const dropinRef = useRef<AdyenDropinHandle>(null)

  // Stable read of `submitting` for the memoized Google Pay callbacks below.
  const submittingRef = useRef(submitting)
  useEffect(() => {
    submittingRef.current = submitting
  }, [submitting])

  useHppTheme(config.theme)

  useEffect(() => {
    let cancelled = false
    createAuthToken(env.INTERNAL_API_HOST, sessionData)
      .then((response) => {
        if (!cancelled) setToken(response.token)
      })
      .catch((error: Error) => {
        if (!cancelled) setTokenError(error)
      })
    return () => {
      cancelled = true
    }
  }, [sessionData])

  const handleReady = () => {
    postToHost('card-payment-setup')
  }

  const handleSubmit = async () => {
    if (!dropinRef.current) return
    if (submitting) return
    setSubmitting(true)

    // One `card-payment-started` per real attempt — emitted after the guard
    // above, so a deduped double-submit stays silent. /hpp-session only,
    // mirroring card-form-valid; the legacy routes' dispatch order is frozen.
    postToHost('card-payment-started')

    try {
      const result = await dropinRef.current.submit()
      // `resultCode` rides along when Adyen produced the outcome; it is absent
      // for the hook's internal fail-safe sentinels, which `refusal` also
      // carries — never forward `refusal` itself to the host.
      if (result.refusal) {
        postToHost('card-payment-failed', result.resultCode ? { resultCode: result.resultCode } : undefined)
      } else {
        const cardholderName = transactionId ? await fetchCardholderName(env.INTERNAL_API_HOST, transactionId) : null
        postToHost('card-payment-completed', cardholderName ? { cardholderName } : undefined)
      }
    } catch (error) {
      const e = error as ErrorWithState
      if (e?.state?.isValid) {
        postToHost('card-payment-error')
      } else {
        postToHost('card-payment-form-invalid')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleApplePaySubmit = async () => {
    if (!dropinRef.current) return
    if (submitting) return
    setSubmitting(true)

    // Same one-per-attempt contract as handleSubmit. This is the motivating
    // case: the Apple Pay tap happens inside this iframe, so the HPP parent has
    // no other signal that an attempt started and cannot show a processing
    // state without it.
    postToHost('card-payment-started')

    try {
      const result = await dropinRef.current.submitApplePay()
      if (result.refusal) {
        postToHost('card-payment-failed', result.resultCode ? { resultCode: result.resultCode } : undefined)
      } else {
        const cardholderName = transactionId ? await fetchCardholderName(env.INTERNAL_API_HOST, transactionId) : null
        postToHost('card-payment-completed', cardholderName ? { cardholderName } : undefined)
      }
    } catch (error) {
      const e = error as ErrorWithState
      if (e?.name === 'CANCEL') {
        // Sheet dismissed by the user — not a failure.
        postToHost('card-payment-cancelled')
      } else if (e?.state?.isValid) {
        postToHost('card-payment-error')
      } else {
        postToHost('card-payment-form-invalid')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Stable identity required — see specs/app/index.md::Apple Pay for why an unmemoized closure here remounts Google Pay.
  const handleGooglePaySubmitAttempt = useCallback(() => {
    if (submittingRef.current) return
    setSubmitting(true)
    // Same one-per-attempt card-payment-started contract as handleSubmit/handleApplePaySubmit.
    postToHost('card-payment-started')
  }, [])

  const handleGooglePayCompleted = useCallback(
    async (result: SessionResult) => {
      // Refusal branching mirrors the documented Card/Apple Pay duplicate — see specs/app/index.md::Cross-frame protocol.
      if (result.refusal) {
        setSubmitting(false)
        postToHost('card-payment-failed', result.resultCode ? { resultCode: result.resultCode } : undefined)
        return
      }
      const cardholderName = transactionId ? await fetchCardholderName(env.INTERNAL_API_HOST, transactionId) : null
      setSubmitting(false)
      postToHost('card-payment-completed', cardholderName ? { cardholderName } : undefined)
    },
    [transactionId]
  )

  const handleGooglePayCancelled = useCallback(() => {
    setSubmitting(false)
    postToHost('card-payment-cancelled')
  }, [])

  const handleGooglePayError = useCallback(() => {
    setSubmitting(false)
    postToHost('card-payment-error')
  }, [])

  // Inbound `card-payment-submit` listener — attached only on /hpp-session.
  // The listener identity is stable for the route's lifetime; it calls the
  // latest handleSubmit via a ref so we don't re-attach on every render.
  const handleSubmitRef = useRef(handleSubmit)
  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  })

  useEffect(() => {
    const expected = expectedHppOrigin()
    if (!expected) {
      console.warn('HPP inbound submit disabled: VITE_HPP_DOMAIN_NAME unset')
      return
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expected) return
      if ((event.data as { type?: string } | null)?.type !== 'card-payment-submit') return
      void handleSubmitRef.current()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-destructive text-sm">Failed to initialize session.</p>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    )
  }

  // `?billingInfo=0` is a hard opt-out that zeroes billing-address collection
  // regardless of the mode resolved by the HPP app (plan JET-2000::Decisions,
  // 2026-07-13 T6 entry). Holder-name capture is an OR against the existing
  // collectPayerInfo knob — never a downgrade of what that knob already
  // requested.
  const effectiveMode: CheckoutFieldMode = collectBillingInfo ? (config.checkoutFieldMode ?? 'default') : 'default'
  const { billingAddressMode, requiresHolderName } = checkoutFieldModeToCardConfig(effectiveMode)

  return (
    <div className="flex min-h-screen flex-col items-center justify-start gap-4">
      <AdyenDropin
        ref={dropinRef}
        internalApiHost={env.INTERNAL_API_HOST}
        authToken={token}
        adyenClientKey={env.ADYEN_CLIENT_KEY}
        adyenEnvironment={env.ENV === 'production' ? 'live' : 'test'}
        sessionType={SessionType.PAYMENT}
        encodedSessionData={sessionData}
        transactionId={transactionId}
        parentHost={config.parentHost}
        showCardholderName={requiresHolderName || config.collectPayerInfo}
        billingAddressMode={billingAddressMode}
        locale={config.lang}
        onValidityChange={(valid) => postToHost(valid ? 'card-form-valid' : 'card-form-invalid')}
        onReady={handleReady}
        onApplePayAvailableChange={setApplePayAvailable}
        // Not gated by `showButton` — the sheet needs an in-frame user gesture.
        applePaySlot={applePayAvailable && <ApplePayButton onClick={handleApplePaySubmit} disabled={submitting} />}
        onGooglePaySubmitAttempt={handleGooglePaySubmitAttempt}
        onGooglePayCompleted={handleGooglePayCompleted}
        onGooglePayCancelled={handleGooglePayCancelled}
        onGooglePayError={handleGooglePayError}
        className="w-full max-w-max"
      >
        {showButton && (
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {config.paymentButtonLabel}
          </Button>
        )}
      </AdyenDropin>
    </div>
  )
}
